import { useState } from 'react';
import { 
    Share2, 
    X, 
    Copy, 
    Check,
    Facebook,
    MessageCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

// Twitter/X icon (custom SVG since lucide doesn't have the new X logo)
const TwitterIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
);

// WhatsApp icon
const WhatsAppIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
);

export const ShareButtons = ({ 
    title, 
    description, 
    url,
    showLabel = true,
    size = 'default', // 'sm', 'default', 'lg'
    variant = 'popup' // 'popup', 'inline'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const shareUrl = url || window.location.href;
    const shareText = `${title}${description ? ` - ${description}` : ''}`;
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    const encodedTitle = encodeURIComponent(title);

    const shareLinks = [
        {
            name: 'Facebook',
            icon: Facebook,
            color: 'bg-[#1877F2] hover:bg-[#166FE5]',
            url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`
        },
        {
            name: 'Twitter',
            icon: TwitterIcon,
            color: 'bg-black hover:bg-gray-800',
            url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`
        },
        {
            name: 'WhatsApp',
            icon: WhatsAppIcon,
            color: 'bg-[#25D366] hover:bg-[#20BD5A]',
            url: `https://wa.me/?text=${encodedText}%20${encodedUrl}`
        }
    ];

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success('Lien copié!');
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast.error('Erreur lors de la copie');
        }
    };

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title,
                    text: description,
                    url: shareUrl
                });
            } catch (error) {
                if (error.name !== 'AbortError') {
                    setIsOpen(true);
                }
            }
        } else {
            setIsOpen(true);
        }
    };

    const handleShareClick = (shareUrl) => {
        window.open(shareUrl, '_blank', 'width=600,height=400,menubar=no,toolbar=no');
    };

    const buttonSize = {
        sm: 'w-8 h-8',
        default: 'w-10 h-10',
        lg: 'w-12 h-12'
    }[size];

    const iconSize = {
        sm: 'w-4 h-4',
        default: 'w-5 h-5',
        lg: 'w-6 h-6'
    }[size];

    if (variant === 'inline') {
        return (
            <div className="flex items-center gap-2 flex-wrap">
                {showLabel && (
                    <span className="text-sm text-muted-foreground font-medium mr-1">Partager:</span>
                )}
                {shareLinks.map((link) => (
                    <button
                        key={link.name}
                        onClick={() => handleShareClick(link.url)}
                        className={`${buttonSize} ${link.color} text-white rounded-full flex items-center justify-center transition-transform hover:scale-110`}
                        title={`Partager sur ${link.name}`}
                        data-testid={`share-${link.name.toLowerCase()}`}
                    >
                        <link.icon className={iconSize} />
                    </button>
                ))}
                <button
                    onClick={handleCopyLink}
                    className={`${buttonSize} bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full flex items-center justify-center transition-transform hover:scale-110`}
                    title="Copier le lien"
                    data-testid="share-copy"
                >
                    {copied ? <Check className={iconSize} /> : <Copy className={iconSize} />}
                </button>
            </div>
        );
    }

    // Popup variant
    return (
        <div className="relative">
            <Button
                size="icon"
                variant="secondary"
                onClick={handleNativeShare}
                className="rounded-full bg-white/90 hover:bg-white"
                data-testid="share-btn"
            >
                <Share2 className="w-4 h-4" />
            </Button>

            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div 
                        className="fixed inset-0 z-40"
                        onClick={() => setIsOpen(false)}
                    />
                    
                    {/* Popup */}
                    <div className="absolute right-0 top-12 z-50 bg-white rounded-2xl shadow-xl border p-4 min-w-[280px] animate-in fade-in slide-in-from-top-2">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-heading font-bold text-lg">Partager</h3>
                            <button 
                                onClick={() => setIsOpen(false)}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                            {title}
                        </p>

                        <div className="flex justify-center gap-3 mb-4">
                            {shareLinks.map((link) => (
                                <button
                                    key={link.name}
                                    onClick={() => {
                                        handleShareClick(link.url);
                                        setIsOpen(false);
                                    }}
                                    className={`w-12 h-12 ${link.color} text-white rounded-full flex items-center justify-center transition-transform hover:scale-110`}
                                    title={`Partager sur ${link.name}`}
                                    data-testid={`share-${link.name.toLowerCase()}`}
                                >
                                    <link.icon className="w-6 h-6" />
                                </button>
                            ))}
                        </div>

                        {/* Copy link section */}
                        <div className="flex items-center gap-2 bg-muted rounded-xl p-2">
                            <input 
                                type="text" 
                                value={shareUrl}
                                readOnly
                                className="flex-1 bg-transparent text-sm truncate outline-none px-2"
                            />
                            <Button
                                size="sm"
                                variant={copied ? "default" : "secondary"}
                                onClick={handleCopyLink}
                                className={`rounded-lg ${copied ? 'bg-green-500 text-white' : ''}`}
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-4 h-4 mr-1" />
                                        Copié!
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4 mr-1" />
                                        Copier
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ShareButtons;
